import type { Highlight } from '../types';
import type { Bookmark, QuickNote, Takeaway } from '../types/agent-context';

export interface ExportData {
  highlights?: Highlight[];
  bookmarks?: Bookmark[];
  quickNotes?: QuickNote[];
  takeaways?: Takeaway[];
  documentTitle?: string;
}

/**
 * Export all annotations as a comprehensive Markdown document.
 * Organizes content by page with highlights, notes, bookmarks, and takeaways.
 */
export function exportAnnotationsAsMarkdown(data: ExportData): string {
  const { highlights = [], bookmarks = [], quickNotes = [], takeaways = [], documentTitle } = data;

  const lines: string[] = [];

  // Header
  lines.push(`# ${documentTitle || 'PDF Study Notes'}`);
  lines.push('');
  lines.push(`Exported on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`);
  lines.push('');

  // Summary statistics
  const stats: string[] = [];
  if (highlights.length > 0) stats.push(`${highlights.length} highlight${highlights.length !== 1 ? 's' : ''}`);
  if (bookmarks.length > 0) stats.push(`${bookmarks.length} bookmark${bookmarks.length !== 1 ? 's' : ''}`);
  if (quickNotes.length > 0) stats.push(`${quickNotes.length} note${quickNotes.length !== 1 ? 's' : ''}`);
  if (takeaways.length > 0) stats.push(`${takeaways.length} takeaway${takeaways.length !== 1 ? 's' : ''}`);

  if (stats.length > 0) {
    lines.push(`**Summary:** ${stats.join(', ')}`);
    lines.push('');
  }

  // Collect all page numbers that have content
  const allPages = new Set<number>();
  highlights.forEach((h) => allPages.add(h.pageNumber));
  bookmarks.forEach((b) => allPages.add(b.pageNumber));
  quickNotes.forEach((n) => allPages.add(n.pageNumber));
  takeaways.forEach((t) => allPages.add(t.pageNumber));

  if (allPages.size === 0) {
    lines.push('_No annotations yet._');
    return lines.join('\n');
  }

  // Sort pages
  const sortedPages = Array.from(allPages).sort((a, b) => a - b);

  // Render each page
  sortedPages.forEach((pageNumber) => {
    lines.push(`## Page ${pageNumber}`);
    lines.push('');

    // Bookmarks for this page
    const pageBookmarks = bookmarks.filter((b) => b.pageNumber === pageNumber);
    if (pageBookmarks.length > 0) {
      pageBookmarks.forEach((bookmark) => {
        lines.push(`**Bookmark:** ${bookmark.label || 'Page ' + bookmark.pageNumber}`);
        if (bookmark.userNote) {
          lines.push(`> ${bookmark.userNote}`);
        }
        if (bookmark.agentContext) {
          lines.push(`> *AI context: "${bookmark.agentContext}"*`);
        }
        lines.push('');
      });
    }

    // Takeaways for this page
    const pageTakeaways = takeaways.filter((t) => t.pageNumber === pageNumber);
    if (pageTakeaways.length > 0) {
      lines.push('### Key Takeaways');
      lines.push('');
      pageTakeaways.forEach((takeaway) => {
        const icon = takeaway.source === 'agent' ? '🤖' : '📝';
        lines.push(`${icon} ${takeaway.summary}`);
        lines.push('');
      });
    }

    // Highlights for this page
    const pageHighlights = highlights.filter((h) => h.pageNumber === pageNumber);
    if (pageHighlights.length > 0) {
      lines.push('### Highlights');
      lines.push('');
      pageHighlights.sort((a, b) => (a.rects[0]?.y ?? 0) - (b.rects[0]?.y ?? 0));
      pageHighlights.forEach((highlight) => {
        const sourceIcon = highlight.source === 'agent' ? '🤖' : '';
        const colorEmoji = {
          yellow: '🟡',
          green: '🟢',
          blue: '🔵',
          pink: '🩷',
          orange: '🟠',
        }[highlight.color];

        lines.push(`${colorEmoji}${sourceIcon} > ${highlight.text}`);
        if (highlight.comment) {
          lines.push(`> **Note:** ${highlight.comment}`);
        }
        lines.push('');
      });
    }

    // Quick notes for this page
    const pageNotes = quickNotes.filter((n) => n.pageNumber === pageNumber);
    if (pageNotes.length > 0) {
      lines.push('### Notes');
      lines.push('');
      pageNotes.forEach((note) => {
        lines.push(`📝 ${note.content}`);
        if (note.agentLastStatement) {
          lines.push(`> *Written while AI discussed: "${note.agentLastStatement}"*`);
        }
        lines.push('');
      });
    }

    lines.push('---');
    lines.push('');
  });

  // Footer
  lines.push('');
  lines.push('*Generated by PDF Reader with Student Learning Features*');

  return lines.join('\n');
}

/**
 * Export all annotations as JSON.
 */
export function exportAnnotationsAsJSON(data: ExportData): string {
  const exportPayload = {
    version: 1,
    exportedAt: new Date().toISOString(),
    documentTitle: data.documentTitle,
    highlights: data.highlights?.map((h) => ({
      ...h,
      createdAt: h.createdAt.toISOString(),
      updatedAt: h.updatedAt.toISOString(),
    })),
    bookmarks: data.bookmarks?.map((b) => ({
      ...b,
      timestamp: b.timestamp.toISOString(),
    })),
    quickNotes: data.quickNotes?.map((n) => ({
      ...n,
      timestamp: n.timestamp.toISOString(),
    })),
    takeaways: data.takeaways?.map((t) => ({
      ...t,
      timestamp: t.timestamp.toISOString(),
    })),
  };

  return JSON.stringify(exportPayload, null, 2);
}

/**
 * Download content as a file.
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export annotations as Markdown and trigger download.
 */
export function downloadAnnotationsAsMarkdown(data: ExportData, filename = 'study-notes.md'): void {
  const content = exportAnnotationsAsMarkdown(data);
  downloadFile(content, filename, 'text/markdown');
}

/**
 * Export annotations as JSON and trigger download.
 */
export function downloadAnnotationsAsJSON(data: ExportData, filename = 'annotations.json'): void {
  const content = exportAnnotationsAsJSON(data);
  downloadFile(content, filename, 'application/json');
}
